import React, { useState } from 'react';
import { UploadContext } from './complexContexts';
import api from '../utils/api';
import { logger } from '../utils/logger';

interface CatalogImage {
  imageId: string;
  file: File | null; // null for existing images loaded in edit mode
  // 'processing' covers the post-S3-upload window while the Lambda image processor
  // generates renditions. 'processing-timeout' is the terminal state when the
  // polling cap is hit without a READY signal — distinct from 'failed' so the UI
  // can offer a "still processing — refresh later" message rather than an error.
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed' | 'processing-timeout';
  thumbnailUrl?: string;
  progress?: number;
  visibility?: 'public' | 'private';
  // Internal-only per-image upload timestamp (issue #1112). Populated from
  // the backend GET response in edit mode; used as legal-proof date for
  // artists. Not shown to public viewers — see catalog_service scrub.
  uploadedAt?: string | null;
  // Metadata surfaced to the image card per the Updated-upload-object mockup.
  // `width`/`height` are captured from `<img>.naturalWidth/Height` after the
  // user picks a file; `fileSize` defaults to `file.size` for new uploads and
  // can be populated from the backend response for existing images.
  filename?: string;
  width?: number;
  height?: number;
  fileSize?: number;
}

const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [catalogId, setCatalogId] = useState<string | null>(null);
  const [catalogTitle, setCatalogTitle] = useState<string>('');
  const [catalogDescription, setCatalogDescription] = useState<string>('');
  const [uploadedImages, setUploadedImages] = useState<CatalogImage[]>([]);
  const [selectedCoverId, setSelectedCoverId] = useState<string | null>(null);
  const [aliCatalogNumber, setAliCatalogNumber] = useState<string | null>(null);
  // Title derived from the first uploaded image's filename (#1225). Set once;
  // consumed by DescribeTab to pre-fill the Title field on new objects only.
  const [suggestedTitle, setSuggestedTitle] = useState<string>('');

  const addImage = (image: CatalogImage) => {
    setUploadedImages(prev => {
      // Check for duplicates by imageId to prevent adding the same image twice
      if (prev.some(img => img.imageId === image.imageId)) {
        return prev;
      }
      return [...prev, image];
    });
  };

  const updateImageStatus = (imageId: string, status: CatalogImage['status'], thumbnailUrl?: string) => {
    setUploadedImages(prev =>
      prev.map(img => {
        if (img.imageId !== imageId) return img;
        // Stamp uploadedAt on the first transition to 'completed' so the
        // image card can render an upload time immediately, before the
        // backend round-trip that would otherwise supply it. Existing
        // edit-mode images already carry uploadedAt from GET; don't clobber.
        const stampUploadedAt = status === 'completed' && !img.uploadedAt;
        return {
          ...img,
          status,
          ...(thumbnailUrl && { thumbnailUrl }),
          ...(stampUploadedAt && { uploadedAt: new Date().toISOString() }),
        };
      })
    );
  };

  /**
   * Patch arbitrary CatalogImage fields (filename, dimensions, fileSize, progress).
   * Used by the upload tab's async dimension probe so the image card metadata
   * caption fills in once `<img>.naturalWidth/Height` resolve.
   */
  const updateImageMetadata = (imageId: string, patch: Partial<Pick<CatalogImage, 'filename' | 'width' | 'height' | 'fileSize' | 'progress'>>) => {
    setUploadedImages(prev =>
      prev.map(img => img.imageId === imageId ? { ...img, ...patch } : img)
    );
  };

  /**
   * Reorder uploadedImages to match the provided imageId sequence. Used by
   * the upload-tab drag-to-reorder UI; persistence is the caller's job
   * (UploadTab debounces a PATCH to the backend).
   */
  const reorderImages = (orderedImageIds: string[]) => {
    setUploadedImages(prev => {
      const byId = new Map(prev.map(img => [img.imageId, img] as const));
      const reordered = orderedImageIds.map(id => byId.get(id)).filter((x): x is CatalogImage => !!x);
      // Preserve any images not represented in the ordered list (defensive).
      const missing = prev.filter(img => !orderedImageIds.includes(img.imageId));
      return [...reordered, ...missing];
    });
  };

  const updateImageVisibility = (imageId: string, visibility: 'public' | 'private') => {
    setUploadedImages(prev =>
      prev.map(img => img.imageId === imageId ? { ...img, visibility } : img)
    );
  };

  const getCoverImage = () => {
    if (selectedCoverId) {
      return uploadedImages.find(img => img.imageId === selectedCoverId) || null;
    }
    return null;
  };

  const createCatalog = async (title: string, ownerId?: string | null, description?: string): Promise<string> => {
    try {
      const params = new URLSearchParams();
      if (ownerId) params.set('owner_id', ownerId);
      const query = params.toString();
      const url = query ? `/api/catalogs?${query}` : '/api/catalogs';
      const response = await api.post(url, { title, description });
      const newCatalogId = response.data.catalogId;

      setCatalogId(newCatalogId);
      setCatalogTitle(title);
      setCatalogDescription(description || '');

      return newCatalogId;
    } catch (error: unknown) {
      logger.error('Error creating catalog', error);
      throw error;
    }
  };

  return (
    <UploadContext.Provider value={{
      catalogId,
      catalogTitle,
      catalogDescription,
      uploadedImages,
      selectedCoverId,
      aliCatalogNumber,
      suggestedTitle,
      setCatalogId,
      setCatalogTitle,
      setCatalogDescription,
      setUploadedImages,
      setSelectedCoverId,
      setAliCatalogNumber,
      setSuggestedTitle,
      addImage,
      updateImageStatus,
      updateImageMetadata,
      updateImageVisibility,
      reorderImages,
      getCoverImage,
      createCatalog
    }}>
      {children}
    </UploadContext.Provider>
  );
};

export { UploadProvider };
export type { CatalogImage };
